import {
  type ExtractPropTypes,
  type PropType,
  computed,
  defineComponent,
} from 'vue'
import {
  createNamespace,
  extend,
  makeObjectProp,
  numericProp,
  unknownProp,
} from '@xzx-design/utils'

// Composables
import { routeProps, useParent, useRoute } from '@xzx-design/hooks'

// Components
import {
  Badge,
  type BadgeProps,
  Icon,
  type IconsComponent,
} from '@xzx-design/components'
import { ACTION_BAR_KEY } from './action-bar'

const [name, bem] = createNamespace('action-bar-icon')

export const actionBarIconProps = extend({}, routeProps, {
  dot: Boolean,
  text: String,
  icon: makeObjectProp<IconsComponent>(null),
  color: String,
  badge: numericProp,
  iconClass: unknownProp,
  badgeProps: Object as PropType<Partial<BadgeProps>>,
  iconPrefix: String,
})

export type ActionBarIconProps = ExtractPropTypes<typeof actionBarIconProps>

export default defineComponent({
  name,

  props: actionBarIconProps,

  setup(props, { slots }) {
    const route = useRoute()

    useParent(ACTION_BAR_KEY)

    const renderIcon = () => {
      const { dot, badge, icon, color, iconClass, badgeProps, iconPrefix } =
        props

      if (slots.icon) {
        return (
          <Badge
            v-slots={{ default: slots.icon }}
            dot={dot}
            class={bem('icon')}
            content={badge}
            {...badgeProps}
          />
        )
      }

      const iconComponent = computed(() => icon)

      return (
        <Icon
          tag="div"
          dot={dot}
          badge={badge}
          color={color}
          class={[bem('icon'), iconClass]}
          badgeProps={badgeProps}
          classPrefix={iconPrefix}
        >
          <iconComponent.value />
        </Icon>
      )
    }

    return () => (
      <div role="button" class={bem()} tabindex={0} onClick={route}>
        {renderIcon()}
        {slots.default ? slots.default() : props.text}
      </div>
    )
  },
})
